// src/services/classTogether.js
// ── Community on release-day classes (docs/plans/release-day-classes-plan.md) ──
//
// Release-day classes have no shared airing, so togetherness comes from two
// quiet signals:
//   • "taken"   — who has done this class (server: /api/class-activity).
//   • "watching" — who is in the class player right now. Each member's open
//     player keeps a tiny presence doc at liveClasses/{classId}/watching/{userId}
//     ({ userId, firstName, lastInitial, at }) fresh with a heartbeat and deletes
//     it on close. Readers ignore anything not refreshed in the last ~2.5 min,
//     so a phone that dies mid-class drops off on its own.
//
// Neither writes class STATE (owner rule 2026-10-06): presence is per-member
// data in a subcollection, the same kind of write as the existing live
// "Also in class" list. Admin never writes presence (she observes).

import { useEffect, useState } from 'react';
import { db, firebaseEnabled } from './firebase';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { setDoc, deleteDoc } from './firestoreWrite';
import { serverNow } from './serverClock';

// ── "taken" ─────────────────────────────────────────────────────────────
const TAKEN_TTL_MS = 60 * 1000;
const takenCache = new Map();   // classId → { at, data }
const takenInflight = new Map(); // classId → Promise
const takenListeners = new Map(); // classId → Set<fn>

export async function fetchClassActivity(classId, getToken, { force = false } = {}) {
    if (!classId || typeof getToken !== 'function') return null;
    const hit = takenCache.get(classId);
    if (!force && hit && Date.now() - hit.at < TAKEN_TTL_MS) return hit.data;
    if (!force && takenInflight.has(classId)) return takenInflight.get(classId);
    const p = (async () => {
        try {
            const token = await getToken();
            if (!token) return null;
            const r = await fetch(`/api/class-activity?classId=${encodeURIComponent(classId)}${force ? '&fresh=1' : ''}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) return hit ? hit.data : null;
            const data = await r.json();
            takenCache.set(classId, { at: Date.now(), data });
            (takenListeners.get(classId) || new Set()).forEach((fn) => { try { fn(data); } catch (_) { /* */ } });
            return data;
        } catch (_) {
            return hit ? hit.data : null;
        } finally {
            takenInflight.delete(classId);
        }
    })();
    takenInflight.set(classId, p);
    return p;
}

// { count, recent:[{userId,name}], youDone } or null while loading / on failure.
// Every component showing the same class shares one cached answer, and a forced
// refresh (after she finishes) updates them all.
export function useClassActivity(classId, getToken, enabled = true) {
    const [data, setData] = useState(() => (classId && takenCache.get(classId)?.data) || null);
    useEffect(() => {
        if (!enabled || !classId) return undefined;
        let alive = true;
        const fn = (d) => { if (alive) setData(d); };
        if (!takenListeners.has(classId)) takenListeners.set(classId, new Set());
        takenListeners.get(classId).add(fn);
        fetchClassActivity(classId, getToken).then((d) => { if (alive && d) setData(d); });
        return () => { alive = false; takenListeners.get(classId)?.delete(fn); };
        // getToken is stable per session (Clerk); classId/enabled drive refetch.
    }, [classId, enabled]);
    return data;
}

// ── "watching" ──────────────────────────────────────────────────────────
const HEARTBEAT_MS = 60 * 1000;
const FRESH_MS = 150 * 1000;

function watchRef(classId, userId) {
    return doc(db, 'liveClasses', classId, 'watching', userId);
}

// Mark this member as in the class player. Returns stop(). Best-effort: any
// failure is silent (presence is decoration, never in the way of the workout).
export function startWatching(classId, { userId, firstName = '', lastInitial = '' }) {
    if (!firebaseEnabled || !db || !classId || !userId) return () => {};
    let stopped = false;
    const write = () => {
        if (stopped) return;
        setDoc(watchRef(classId, userId), {
            userId, firstName: String(firstName).slice(0, 40), lastInitial: String(lastInitial).slice(0, 1), at: serverNow(),
        }).catch(() => {});
    };
    const remove = () => { deleteDoc(watchRef(classId, userId)).catch(() => {}); };
    write();
    const timer = setInterval(write, HEARTBEAT_MS);
    // Tab hidden for a while / closed: drop off. Coming back re-announces.
    const onVis = () => { if (document.visibilityState === 'hidden') remove(); else write(); };
    const onHide = () => remove();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    return () => {
        stopped = true;
        clearInterval(timer);
        document.removeEventListener('visibilitychange', onVis);
        window.removeEventListener('pagehide', onHide);
        remove();
    };
}

// Others in the class player right now (excluding `selfId`), freshest first.
export function useClassWatchers(classId, selfId, enabled = true) {
    const [rows, setRows] = useState([]);
    const [now, setNow] = useState(() => serverNow());
    useEffect(() => {
        if (!enabled || !firebaseEnabled || !db || !classId) { setRows([]); return undefined; }
        const unsub = onSnapshot(collection(db, 'liveClasses', classId, 'watching'),
            (snap) => { setRows(snap.docs.map((d) => d.data())); setNow(serverNow()); },
            () => setRows([]));
        // Re-evaluate freshness even when nothing is written (someone's phone died).
        const t = setInterval(() => setNow(serverNow()), 30 * 1000);
        return () => { unsub(); clearInterval(t); };
    }, [classId, enabled]);
    return rows
        .filter((r) => r && r.userId && r.userId !== selfId && typeof r.at === 'number' && now - r.at < FRESH_MS)
        .sort((a, b) => b.at - a.at);
}

export function shortMemberName(first, lastInitial) {
    const f = (first || '').trim();
    const l = (lastInitial || '').trim();
    if (!f) return '';
    return l ? `${f} ${l.charAt(0).toUpperCase()}` : f;
}
